import React, { useState } from 'react';
import PropTypes from 'prop-types';
import qs from 'qs';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { makeStyles } from '@material-ui/core/styles';
import Hidden from '@material-ui/core/Hidden';
import Grid from '@material-ui/core/Grid';
import ButtonBase from '@material-ui/core/ButtonBase';
import Typography from '@material-ui/core/Typography';
import {
  makeSelectAllowSignIn,
  makeSelectClientDetails,
  makeSelectOAuthOptions,
} from 'containers/Main/selectors';
import AppContainer from 'components/AppContainer';
import AuthenticationImage from 'components/Authentication/AuthenticationImage';
import BackButton from 'components/Authentication/BackButton';
import FormContainer from 'components/Authentication/FormContainer';
import ContentBlock from 'components/ContentBlock';
import { CONTENT_BLOCK_POSITIONS } from 'components/ContentBlock/utils';
import PageIntroBlock from 'components/PageIntroBlock';
import PasswordTextField from 'components/PasswordTextField';
import ProfileTextField from 'components/ProfileTextField';
import SAMLButton from 'components/SAMLButton';
import FacebookButton from 'components/SocialButtons/FacebookButton';
import GoogleButton from 'components/SocialButtons/GoogleButton';
import MicrosoftButton from 'components/SocialButtons/MicrosoftButton';
import useSiteCopySelector from 'components/useSiteCopySelector';
import Colors from 'utils/colors';
import { emailRegex } from 'utils/stringUtils';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    marginTop: 24,
  },
  checkboxApply: {
    padding: '0',
    paddingRight: 5,
  },
  textFieldContainer: {
    '& label': {
      fontSize: '1.063rem',
    },
    '& input': {
      fontSize: '1.063rem',
    },
  },
  button: {
    background: theme.palette.text.secondary,
    color: '#ffffff',
    width: '100%',
    padding: 12,
    marginTop: 8,
    marginBottom: 5,
    textTransform: 'uppercase',
    '&:hover': {
      opacity: 0.8,
    },
    ...theme.typography.pSmallBold,
  },
  socialContainer: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
  },
  midContainer: {
    position: 'relative',
    display: 'flex',
    width: '100%',
    alignItems: 'center',
  },
  midContainerLabel: {
    paddingLeft: 8,
    paddingRight: 8,
    width: '50%',
    textAlign: 'center',
    ...theme.typography.pxSmall,
    fontSize: '0.875rem',
  },
  grayBar: {
    flex: '1',
    backgroundColor: 'rgba(0, 0, 0, 0.12)',
    height: 1,
  },
  link: {
    marginLeft: 5,
    textDecoration: 'none',
    color: Colors.link,
  },
  forgotLink: {
    textAlign: 'end',
    width: '100%',
    textDecoration: 'none',
    color: Colors.link,
    marginTop: 8,
  },
  linkBtn: {
    textDecoration: 'underline',
    cursor: 'pointer',
  },
  form: {
    paddingTop: 16,
    [theme.breakpoints.up('md')]: {
      maxWidth: 440,
    },
    [theme.breakpoints.down('sm')]: {
      paddingTop: 24,
    },
  },
  passwordWrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

const LoginForm = ({
  onFacebookLogin,
  onGoogleLogin,
  onMicrosoftLogin,
  onSubmit,
}) => {
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState([]);
  const allowSignIn = useSelector(makeSelectAllowSignIn());
  const clientDetails = useSelector(makeSelectClientDetails());
  const authOptions = useSelector(makeSelectOAuthOptions());
  const [
    signInSiteCopy,
    signUpSiteCopy,
    authImageSiteCopy,
  ] = useSiteCopySelector(['sign-in', 'sign-up', 'authentication-pages-image']);
  const classes = useStyles({
    background: _get(authImageSiteCopy, 'pageCopy.mobile-background.1x'),
    background2x: _get(authImageSiteCopy, 'pageCopy.mobile-background.2x'),
  });

  const clientName = _get(clientDetails, 'name');
  const { redirect } = qs.parse(location.search.slice(1));

  const handleFormChange = e => {
    if (e.target.name === 'password') {
      setPassword(e.target.value);
      if (error.includes('password')) {
        setError(error.filter(item => item !== 'password'));
      } else if (e.target.value === '') {
        setError(error.concat('password'));
      }
    } else if (e.target.name === 'email') {
      setEmail(e.target.value);
      if (error.includes('email')) {
        setError(error.filter(item => item !== 'email'));
      } else if (e.target.value === '') {
        setError(error.concat('email'));
      }
    }
  };

  const handleSubmit = e => {
    e.preventDefault();

    const formErrors = [];

    if (!emailRegex.test(email)) {
      formErrors.push('email');
    }
    if (password === '') {
      formErrors.push('password');
    }

    if (formErrors.length === 0) {
      onSubmit({ email, password });
    } else {
      setError(formErrors);
    }
  };

  return (
    <Grid container direction="row">
      <Hidden smDown>
        <AuthenticationImage />
      </Hidden>
      <FormContainer>
        <AppContainer direction="row" spacing={0}>
          <BackButton label={_get(signInSiteCopy, 'pageCopy.backNavigation')} />
          {allowSignIn ? (
            <Grid item xs={12} className={classes.form}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h4" color="primary" gutterBottom>
                    {_get(signInSiteCopy, 'section1Header')}
                  </Typography>
                  <PageIntroBlock type="common" page="sign-in" />
                  <ContentBlock
                    type="common-page"
                    position={CONTENT_BLOCK_POSITIONS.BELOW_HERO_INTRO}
                  />
                  {authOptions.cm && !_isEmpty(clientDetails) && (
                    <form
                      className={classes.container}
                      onSubmit={handleSubmit}
                      noValidate
                    >
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <ProfileTextField
                            field="email"
                            plain
                            value={email}
                            onChange={handleFormChange}
                            pattern="^\\S+@\\S+\\.\\S+$"
                            required
                            type="email"
                            error={error.includes('email')}
                          />
                        </Grid>
                        <Grid item xs={12} className={classes.passwordWrapper}>
                          <PasswordTextField
                            value={password}
                            onChange={handleFormChange}
                            plain
                            required
                            hideVisibilityToggle
                            error={
                              error.includes('password')
                                ? _get(signUpSiteCopy, 'pageCopy.passwordError')
                                : false
                            }
                          />
                          <Link
                            to={`/forgot_password${
                              redirect ? `?redirect=${redirect}` : ''
                            }`}
                            className={classes.forgotLink}
                          >
                            {_get(
                              signInSiteCopy,
                              'pageCopy.forgotPasswordLabel',
                            )}
                          </Link>
                        </Grid>
                        <Grid item xs={12}>
                          <ButtonBase className={classes.button} type="submit">
                            {_get(signInSiteCopy, 'pageCopy.cmLabel')}
                          </ButtonBase>
                        </Grid>
                      </Grid>
                    </form>
                  )}
                </Grid>
                {(authOptions.facebook ||
                  authOptions.google ||
                  authOptions.ms) &&
                  authOptions.cm &&
                  !_isEmpty(clientDetails) && (
                    <Grid item xs={12}>
                      <div className={classes.midContainer}>
                        <div className={classes.grayBar} />
                        <Typography
                          variant="subtitle2"
                          align="center"
                          className={classes.midContainerLabel}
                        >
                          {_get(signInSiteCopy, 'section2CallToAction')}
                        </Typography>
                        <div className={classes.grayBar} />
                      </div>
                    </Grid>
                  )}
                {(authOptions.facebook ||
                  authOptions.google ||
                  authOptions.ms) && (
                  <Grid item xs={12} className={classes.socialContainer}>
                    {authOptions.google && (
                      <GoogleButton
                        onClick={onGoogleLogin}
                        text={_get(signInSiteCopy, 'pageCopy.googleLabel')}
                      />
                    )}
                    {authOptions.facebook && (
                      <FacebookButton
                        onClick={onFacebookLogin}
                        text={_get(signInSiteCopy, 'pageCopy.facebookLabel')}
                      />
                    )}
                    {authOptions.ms && (
                      <MicrosoftButton
                        onClick={onMicrosoftLogin}
                        text={_get(signInSiteCopy, 'pageCopy.microosftLabel')}
                      />
                    )}
                    <SAMLButton />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography variant="overline" display="block">
                    {_get(signInSiteCopy, 'pageCopy.signUpLabel')}
                    <Link
                      to={`/signup${redirect ? `?redirect=${redirect}` : ''}`}
                      className={classes.link}
                    >
                      {_get(signInSiteCopy, 'pageCopy.signUpLinkLabel')}
                    </Link>
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          ) : (
            <Typography variant="overline" align="center" display="block">
              {`User accounts are not currently supported for ${clientName}. Please check back later.`}
            </Typography>
          )}
        </AppContainer>
      </FormContainer>
    </Grid>
  );
};

LoginForm.propTypes = {
  onFacebookLogin: PropTypes.func,
  onGoogleLogin: PropTypes.func,
  onMicrosoftLogin: PropTypes.func,
  onSubmit: PropTypes.func,
};

export default LoginForm;
